import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { odata } from '@bingads-webui/http-util';
import { getMessageFromErrors } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { mockedTags } from './mock-data';

const campaignEDM = edmCampaign.types.resolve('Model.Campaign');
const campaignResource = new campaignEDM.ResourceIdentifier();
const bulkUpdateCampaigns = campaignResource.bulkUpdate.bind(campaignResource);

const emailInfo = {
  UET: {
    EmailNotificationId: 388,
    EmailTemplateName: 'UetTagInstructionsEmail',
  },
  Event: {
    EmailNotificationId: 389,
    EmailTemplateName: 'UetTagAndEventTagInstructionsEmail',
  },
  UetTagInstructionsEmailV2: {
    EmailNotificationId: 589,
    EmailTemplateName: 'UetTagInstructionsEmailV2',
  },
  UetTagAndEventTagInstructionsEmailV2: {
    EmailNotificationId: 590,
    EmailTemplateName: 'UetTagAndEventTagInstructionsEmailV2',
  },
};

export const getDefaultActionForGoalCategory = (goalCategory) => {
  if (_.isEmpty(goalCategory)) {
    return '';
  }
  // MT has a typo. Fix it before using it to generate event name.
  const fixedGoalCategory = goalCategory === 'Subcribe' ? 'Subscribe' : goalCategory;

  const lowerCaseCategory = fixedGoalCategory.toLowerCase();
  const outputWords = [];
  let startIdx = 0;
  Array.from(fixedGoalCategory).forEach((char, idx) => {
    if (char !== lowerCaseCategory[idx]) {
      outputWords.push(lowerCaseCategory.slice(startIdx, idx));
      startIdx = idx;
    }
  });
  outputWords.push(lowerCaseCategory.slice(startIdx, lowerCaseCategory.length));
  return _.reject(outputWords, word => _.isEmpty(word)).join('_');
};

export const updateGoalObjectForClaritySetup = (existingGoal) => {
  const goal = { ...existingGoal };
  let isUpdateRequired = false;

  const nulledParameters = ['Category', 'Value'];
  nulledParameters.forEach((parameter) => {
    if (goal[parameter]) {
      goal[parameter] = null;
      isUpdateRequired = true;
    }
  });

  // If existing action, allow user to keep using it.
  if (!goal.Action || (goal.ActionOperator && goal.ActionOperator === 'RegularExpression')) {
    goal.Action = getDefaultActionForGoalCategory(goal.GoalCategory);
    goal.ActionOperator = 'EqualsTo';
    isUpdateRequired = true;
  }

  return { goal, isUpdateRequired };
};

export const postEmail = (cid, aid, token, emailType, emailAddresses, emailContent) => {
  const { EmailNotificationId, EmailTemplateName } = emailInfo[emailType];

  const data = {
    EmailContent: Object.entries(emailContent).map(([k, v]) => ({ Key: k, Value: v })),
    EmailAddresses: emailAddresses,
    EmailTemplateName,
    EmailNotificationId,
    ToAdvertiser: true,
  };
  const url = aid ? `${window.location.origin}/ODataApi/Advertiser/V2/Customers(${cid})/Accounts(${aid})/SendEmail` :
    `${window.location.origin}/ODataApi/Advertiser/V2/Customers(${cid})/Accounts/SendEmail`;
  try {
    return odata.post({
      url,
      data,
      headers: {
        Authorization: `CCMTSmallToken ${token}`,
      },
    }).then((res) => {
      if (res && res.value) {
        return res;
      }
      return Promise.reject(res);
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

export const postClarityEventInfo = (cid, aid, goalId, token, clarityEventInfo) => {
  const data = {
    ClarityEventInfo: clarityEventInfo,
  };
  const url = `${window.location.origin}/ODataApi/Advertiser/V2/Customers(${cid})/Accounts(${aid})/Goals(${goalId})/Default.SetClarityEventInfo()`;
  try {
    return odata.post({
      url,
      data,
      headers: {
        Authorization: `CCMTSmallToken ${token}`,
      },
    }).then((res) => {
      if (res && res.value) {
        return res;
      }
      return Promise.reject(res);
    });
  } catch (e) {
    return Promise.reject(e);
  }
};

export const getClarityEventInfo = (cid, aid, goalId, token) => {
  const url = `${window.location.origin}/ODataApi/Advertiser/V2/Customers(${cid})/Accounts(${aid})/Goals(${goalId})/Default.GetClarityEventInfo()`;
  return odata.get({
    url,
    headers: {
      Authorization: `CCMTSmallToken ${token}`,
    },
  });
};

const makeid = () => {
  const idlength = 6;
  const result = [];
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < idlength; i += 1) {
    result.push(characters.charAt(Math.floor(Math.random() *
charactersLength)));
  }
  return result.join('');
};

const postTagPromise = (cid, aid, isForMock = false) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .Tags
  .post({
    Description: 'AutoCreated default tag',
    Name: `DefaultTag${isForMock ? makeid() : ''}`,
  }).toPromise().then((response) => {
    if (response && response.Id) {
      return [{ ...response, isAutoCreated: true }];
    }
    throw new Error('Could not create tag');
  });

const updateTag = (cid, aid, tagId, payload) => {
  const edmCustomer = edmCampaign.Customers.$withKey(cid);
  const edmHolder = aid ? edmCustomer.Accounts.$withKey(aid) : edmCustomer;
  return edmHolder.Tags.update(tagId, payload)
    .then((response) => {
      if (response.Id) {
        return response;
      }
      throw response;
    });
};

export const enableClarityForTag = (scope, serverContext, tag) => {
  const { cid, aid } = (scope && scope.values) || {};
  let customerId = cid;
  if (_.isFinite(aid)) {
    // customer level Tag belongs to scope cid; account level tag should use advcid
    const { CurrentAccount: currentAccount } = serverContext;
    customerId = currentAccount.AdvertiserCustomerID;
    if (!customerId) {
      throw new Error('customer Id is undefined');
    }
  }

  return updateTag(customerId, aid, tag.Id, { Name: tag.Name, IsClarityTagEnabled: 1 });
};

export const getTags = (cid, aid) => {
  if (cid && aid) {
    return edmCampaign
      .Customers.$withKey(cid)
      .Accounts.$withKey(aid)
      .Tags
      .get({
        top: 2000,
        select: ['Id', 'Name', 'Owner', 'TrackingCode', 'IsPreferredTag', 'IsClarityTagEnabled'],
      }).toPromise().then((tagResponse) => {
        if (tagResponse && tagResponse.value) {
          if (tagResponse.value.length > 0) {
            return tagResponse.value;
          }
          return postTagPromise(cid, aid);
        }

        throw new Error(`Could not fetch tags:${JSON.stringify(tagResponse)}`);
      });
  }
  return new Promise((resolve) => {
    resolve(mockedTags.value);
  });
};

export const getExternalIntegrations = (cid, aid, tagId) => {
  if (cid && aid) {
    return edmCampaign
      .Customers.$withKey(cid)
      .Accounts.$withKey(aid)
      .Tags.$withKey(tagId)['Default.GetExternalIntegrations']
      .$makeCall({})
      .toPromise().then(response => ({ ...response, hasExternalIntegrations: response.NumberOfEntities > 0 }));
  }
  return new Promise((resolve) => {
    resolve(mockedTags.value);
  });
};

export const getPreferredTag = tags => _.find(tags, item => item.IsPreferredTag) || _.find(tags, item => item.isAutoCreated);

export const findTagById = (tags, tagId) => _.find(tags, item => item.Id === tagId);

export const getGoal = (goalId, cid, aid) =>
  goalId && edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Goals.$withKey(goalId)
    .get({
      select: ['Tag', 'ServingExternalChannelInfos'],
    });

const createGoal = (payload, cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Goals.post(payload);

const updateGoal = (goalId, payload, cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Goals.update(goalId, payload);

export const saveLinkedInAssociations = (goalObj, wizardContext) => {
  const {
    permissions,
    associatedCampaigns: {
      current: currentAssociatedCampaigns,
      initial: initialAssociatedCampaigns,
    },
  } = wizardContext;

  const formatAssociation = (campaignId, goal, action) => ({
    Id: campaignId,
    CampaignConversionGoalAssociations: [{
      CampaignId: campaignId,
      Action: action,
      GoalId: Number(goal.Id),
      Name: goal.Name,
      SubType: 'Goal',
    }],
  });

  const currentIds = _.pluck(currentAssociatedCampaigns, 'Id');
  const initialIds = _.pluck(initialAssociatedCampaigns, 'Id');

  const idsToAdd = _.difference(currentIds, initialIds);
  const idsToRemove = _.difference(initialIds, currentIds);

  const dataToSave = [
    ...idsToAdd.map(campaignId => formatAssociation(campaignId, goalObj, 'Add')),
    ...idsToRemove.map(campaignId => formatAssociation(campaignId, goalObj, 'Delete')),
  ];

  const isLinkedInEnabled = _.get(permissions, ['dynamic', 'LinkedInCampaigns'], false);

  if (!isLinkedInEnabled || _.isEmpty(dataToSave)) {
    return Promise.resolve();
  }

  return bulkUpdateCampaigns(dataToSave);
};

export const processPayload = (goalObject, permissions, deleteClarityEvent) => {
  const rawPayload = { ...goalObject };
  if (!_.isEmpty(goalObject.Tag)) {
    rawPayload.Tag = {
      Id: goalObject.Tag.Id,
    };
  }

  // delete invalid properties
  if (!_.isUndefined(rawPayload.Id)) {
    const invalidProps = ['CampaignAnalyticsGoalName', 'FlexTagScript', 'GoalPerformanceMetrics', 'TrackingStatus', '@odata.context'];
    _.each(invalidProps, (prop) => {
      delete rawPayload[prop];
    });
  }
  // convert legacy null duration to 0
  if (rawPayload.Duration === null) {
    rawPayload.Duration = 0;
  }
  // delete tags
  delete rawPayload.tags;
  const goalType = rawPayload.Type;
  // delete empty event params
  if (goalType === 'EventGoal') {
    const valueArray = ['Category', 'Action', 'Label', 'Value'];
    const operatorArray = ['CategoryOperator', 'ActionOperator', 'LabelOperator', 'ValueOperator'];
    _.each(valueArray, (value, index) => {
      if (_.isNull(rawPayload[value])) {
        delete rawPayload[value];
        delete rawPayload[operatorArray[index]];
      }
    });
  }
  // delete viewthroughconversion for ineligible goal types
  const nonVTGoalTypes = ['ApplicationInstallGoal', 'OfflineConversionGoal', 'InStoreTransactionGoal', 'InStoreVisitGoal'];
  if (nonVTGoalTypes.indexOf(goalType) > -1 || !permissions.IsViewThroughConversionEnabled || rawPayload.ViewThroughLookbackWindowinMinutes === 0) {
    delete rawPayload.ViewThroughLookbackWindowinMinutes;
  }
  // delete currency null code and value
  const goalTypesDisallowedCurrency = [
    'DurationGoal',
    'PageViewsPerVisitGoal',
    'ApplicationInstallGoal',
    'SmartGoal'];
  const goalValueType = rawPayload.GoalValueType;
  const goalValue = rawPayload.GoalValue;
  if (goalValueType === 'NoValue') {
    delete rawPayload.CurrencyCode;
    delete rawPayload.GoalValue;
  } else if (goalValueType === 'FixedValue' && goalTypesDisallowedCurrency.indexOf(goalType) > -1) {
    delete rawPayload.CurrencyCode;
  } else {
    rawPayload.GoalValue = _.isUndefined(goalValue) || _.isNull(goalValue) ? 1 : goalValue;
    rawPayload.CurrencyCode = rawPayload.CurrencyCode === null ? 'USD' : rawPayload.CurrencyCode;
  }
  if (deleteClarityEvent) {
    rawPayload.ClarityEventDefinitionId = null;
    rawPayload.ClarityEventDefinitionObject = null;
  }
  // add entity type
  rawPayload['@odata.type'] = `#Model.${goalType}`;
  return rawPayload;
};

export const saveGoal = (payload, wizardContext) => {
  const {
    aid, cid, logGoalSaved, isDemo,
  } = wizardContext;

  if (isDemo) {
    return Promise.resolve({ Id: 1, ...payload });
  }

  const savePromise = _.isUndefined(payload.Id)
    ? createGoal(payload, cid, aid).toPromise()
      .then((response) => {
        if (_.isFinite(response.value)) {
          const goal = { ...payload, Id: response.value };
          logGoalSaved(goal);
          return goal;
        }
        throw response;
      })
    : updateGoal(payload.Id, payload, cid, aid)
      .then((response) => {
        if (response.value === 0) {
          logGoalSaved(payload);
          return payload;
        }
        throw response;
      });

  return savePromise.then(goalObj => saveLinkedInAssociations(goalObj, wizardContext).then(() => goalObj));
};

export const getErrorMessage = (response, i18n) => {
  if (response && response.body) {
    const errorMessage = getMessageFromErrors(i18n, response.body.value);
    if (errorMessage) {
      return errorMessage;
    }
  }
  return i18n.getString(_TL_('We\'ve encountered an error while saving this goal. Please try again or contact support for assistance.'));
};

export const readPmaxCampaigns = (cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns
    .get({
      filter: {
        where: 'CampaignType eq \'PerformanceMax\'',
      },
    })
    .toPromise();

export const readLinkedInCampaigns = (cid, aid) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns
    .get({
      select: ['Id', 'Name', 'DeliveryStatus'],
      filter: {
        where: 'CampaignType eq \'LinkedIn\'',
      },
    })
    .toPromise();

export const getAssociatedCampaignIds = goalObj =>
  _.chain(goalObj)
    .get(['ServingExternalChannelInfos', 'LinkedIn', 'CampaignConversionGoals'], [])
    .pluck('CampaignId')
    .value();


export const readAssociatedCampaigns = (cid, aid, goalObj) => {
  const cmpIds = getAssociatedCampaignIds(goalObj);

  const formattedCampaignIds = cmpIds.join(', ');

  return edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns
    .get({
      select: ['Id', 'Name', 'DeliveryStatus'],
      filter: { where: `Id in [${formattedCampaignIds}]` },
    })
    .toPromise();
};

